import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as snippet from '@segment/snippet';
import { ApiContext, WizardContext } from '../utils/contexts.js';
import { STEP_START } from '../utils/CONSTANTS.js';
import WizardNavigation from './WizardNavigation.js';
import formwizardApi, { setInstance } from '../services/api/formwizardApi.js';
import { getComponents } from '../components/index.js';
import { jsx } from '@emotion/react';
function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
var evalGlobal = eval;

/**
 * Define how often the data request will retry if something went wrong.
 * Value of '2' mean 3 requests in total.
 * @type {number}
 */
var MAX_RETRIES = 2;

/**
 * Define the time (in ms) to wait before request again in case of an error.
 * @type {number}
 */
var REQUEST_RETRY_WAITING_TIME = 3000;

/**
 * The Formwizard.
 *
 * This is the entry point of the library where everything global is set up.
 * At the same time, this is the point that is exposed to consumers.
 *
 * What it does:
 *   - Sets up the contexts
 *   - Calls the API
 *   - Sets up tracking
 *   - Initiates everything / rendering
 *
 * @param dialogId
 * @param leadId
 * @param recruiterId
 * @param config
 * @param isLocal
 */
var Formwizard = function Formwizard(_ref) {
  var _data$options;
  var dialogId = _ref.dialogId,
    dialogConfig = _ref.dialogConfig,
    leadId = _ref.leadId,
    recruiterId = _ref.recruiterId,
    customComponents = _ref.components,
    config = _ref.config,
    isLocal = _ref.isLocal,
    position = _ref.position,
    variant = _ref.variant,
    doTrack = _ref.doTrack,
    extendedContent = _ref.extendedContent,
    appsignal = _ref.appsignal;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    data = _useState2[0],
    setData = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    error = _useState4[0],
    setError = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    loading = _useState6[0],
    setLoading = _useState6[1];
  var _useState7 = useState(0),
    _useState8 = _slicedToArray(_useState7, 2),
    requestRetries = _useState8[0],
    setRequestRetries = _useState8[1];
  var _useState9 = useState(getComponents(customComponents)),
    _useState0 = _slicedToArray(_useState9, 1),
    components = _useState0[0];
  var _useState1 = useState(null),
    _useState10 = _slicedToArray(_useState1, 2),
    currentLine = _useState10[0],
    setCurrentLine = _useState10[1];
  var _useState11 = useState(STEP_START),
    _useState12 = _slicedToArray(_useState11, 2),
    currentStep = _useState12[0],
    setCurrentStep = _useState12[1];
  var _useState13 = useState({
      gender: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    }),
    _useState14 = _slicedToArray(_useState13, 2),
    userData = _useState14[0],
    _setUserData = _useState14[1];
  var _useState15 = useState({}),
    _useState16 = _slicedToArray(_useState15, 2),
    stepData = _useState16[0],
    setStepData = _useState16[1];
  var _useState17 = useState(null),
    _useState18 = _slicedToArray(_useState17, 2),
    redirectUrl = _useState18[0],
    setRedirectUrl = _useState18[1];

  /**
   * Merge normal wizard data with extended hard-coded wizard data if available
   * @param {object} newDialogConfig
   */
  var setDialogData = function setDialogData(newDialogConfig) {
    setData(_objectSpread(_objectSpread({}, newDialogConfig), extendedContent));
  };

  // Setup segment / analytics if not already initialized from app and key exists.
  useEffect(function () {
    var _window, _config$analytics;
    if (doTrack && !((_window = window) !== null && _window !== void 0 && _window.analytics) && config !== null && config !== void 0 && (_config$analytics = config.analytics) !== null && _config$analytics !== void 0 && _config$analytics.segmentKey) {
      var opts = {
        apiKey: config.analytics.segmentKey,
        // We do NOT want to track pages because it causes a segment call overload because the Wizard is built into
        // every page of hausgold.de
        //
        // We track the steps via segment events inside the formwizard itself.
        page: false
      };
      var segmentScript;
      if (isLocal) {
        segmentScript = snippet.max(opts);
      } else {
        segmentScript = snippet.min(opts);
      }

      // Needed to run segment script (delivered as string) which enables window.analytics
      evalGlobal(segmentScript);
    }
  }, []);

  // Create instance of formwizard API with given config
  useEffect(function () {
    setInstance(config, {
      variant: variant
    }, appsignal, isLocal);
  }, []);

  // Load wizard data
  useEffect(function () {
    // Ensure API is initialized and dialog id is defined and dialog itself is not given.
    if (formwizardApi && dialogId && !dialogConfig) {
      setLoading(true);
      formwizardApi().getDialog({
        id: dialogId
      }).then(function (response) {
        return response === null || response === void 0 ? void 0 : response.data;
      }).then(function (dialogData) {
        setDialogData(dialogData);
        setLoading(false);
      })["catch"](function (_error) {
        // Count up the amount of failed requests.
        if (requestRetries < MAX_RETRIES) {
          setTimeout(function () {
            setRequestRetries(requestRetries + 1);
          }, REQUEST_RETRY_WAITING_TIME);
          return;
        }
        if (appsignal) {
          appsignal.send(appsignal.createSpan(function (span) {
            span.setAction('User was redirected to error page');
            span.setError(_error);
            span.setTags({
              retry: requestRetries
            });
          }));
        }

        // If retry did not help stop and tell it to the user...
        setError(_error);
        setLoading(false);
      });

      // If dialog itself is given use it directly
    } else if (dialogConfig) {
      setDialogData(dialogConfig);
    }
  }, [formwizardApi, dialogId, dialogConfig, requestRetries]);
  var bgColor = data !== null && data !== void 0 && (_data$options = data.options) !== null && _data$options !== void 0 && _data$options.background_color ? data.options.background_color : 'inherit';

  // Produce a custom window event with the formwizard dialog configured
  // background color, in order to be able to set it on the Next.js surrounding
  // page
  useEffect(function () {
    var _window2;
    var event = new CustomEvent('hg-fw-background-color', {
      detail: bgColor
    });
    (_window2 = window) === null || _window2 === void 0 || _window2.dispatchEvent(event);
  }, [bgColor]);
  return jsx(ApiContext.Provider, {
    value: {
      error: error,
      isLoading: loading,
      data: data,
      dialogId: dialogId,
      config: config,
      // Used for extended wizard
      redirectUrl: redirectUrl,
      setRedirectUrl: setRedirectUrl
    }
  }, jsx(WizardContext.Provider, {
    value: {
      components: components,
      leadId: leadId,
      recruiterId: recruiterId,
      dialogId: dialogId,
      position: position,
      currentLine: currentLine,
      currentStep: currentStep,
      userData: userData,
      stepData: stepData,
      setStepData: setStepData,
      setUserData: function setUserData(newUserData) {
        var _window3;
        // Track user data to segment
        (_window3 = window) === null || _window3 === void 0 || (_window3 = _window3.analytics) === null || _window3 === void 0 || _window3.identify(newUserData === null || newUserData === void 0 ? void 0 : newUserData.email, newUserData);

        // Set user data in state
        _setUserData(newUserData);
      },
      setCurrentLine: setCurrentLine,
      setCurrentStep: setCurrentStep
    }
  }, jsx("div", {
    id: "variant_".concat(variant, "_dialog_").concat(dialogId)
  }, jsx(WizardNavigation, {
    bgColor: bgColor
  }))));
};
Formwizard.propTypes = {
  dialogId: PropTypes.string,
  dialogConfig: PropTypes.object,
  leadId: PropTypes.string,
  recruiterId: PropTypes.string,
  variant: PropTypes.string,
  components: PropTypes.shape({
    WizardHeading: PropTypes.func
  }),
  config: PropTypes.shape({
    api: PropTypes.shape({
      urls: PropTypes.shape({
        wizard: PropTypes.shape({
          http: PropTypes.string.isRequired
        }).isRequired
      }).isRequired
    }).isRequired,
    analytics: PropTypes.shape({
      segmentKey: PropTypes.string
    })
  }).isRequired,
  isLocal: PropTypes.bool,
  doTrack: PropTypes.bool,
  position: PropTypes.oneOf(['right', 'top', 'header']),
  extendedContent: PropTypes.object,
  appsignal: PropTypes.object
};
Formwizard.defaultProps = {
  dialogId: null,
  dialogConfig: null,
  leadId: null,
  recruiterId: null,
  isLocal: false,
  components: {},
  position: null,
  variant: 'default',
  extendedContent: {},
  doTrack: true,
  appsignal: null
};
Formwizard.displayName = 'Formwizard';
export { Formwizard as default };